import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, CheckCircle2, ExternalLink, Loader2, RefreshCw, Filter,
  ChevronLeft, ChevronRight, FileText,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { listReviewOutcomes } from './reviewOutcomesApi';
import { ReviewOutcomesAnalytics } from './ReviewOutcomesAnalytics';

/**
 * Day 8 — Review Outcomes Explorer Page
 *
 * Read-only, scope-filtered search over completed chart reviews.
 * Guarded by clinical_chart_review_enabled + charts_module_enabled.
 * No writes, no exports, no automation.
 */

const LIMIT = 25;
const ANALYTICS_CAP = 100; // matches backend MAX_LIMIT

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

function toDateInput(iso) {
  if (!iso) return '';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
}

function clamp(text, max = 160) {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export default function ReviewOutcomesExplorerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canView = perms.includes('participant_charts.view');

  const [flagsChecked, setFlagsChecked] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // filters (controlled inputs)
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [chartType, setChartType] = useState('all');

  // applied filters (what's sent to API — updated on Apply / pagination)
  const [applied, setApplied] = useState({ q: '', date_from: '', date_to: '', chart_type: '' });
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Day 10 — analytics dataset (independent of table pagination).
  // Fetched once per applied-filters change with limit=ANALYTICS_CAP, offset=0.
  const [analyticsItems, setAnalyticsItems] = useState([]);
  const [analyticsTotal, setAnalyticsTotal] = useState(0);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // flag gate
  useEffect(() => {
    let cancelled = false;
    api.get('/production/feature-flags/mine')
      .then(r => {
        if (cancelled) return;
        const flags = r?.data?.data || {};
        setEnabled(!!flags.charts_module_enabled && !!flags.clinical_chart_review_enabled);
      })
      .catch(() => { if (!cancelled) setEnabled(false); })
      .finally(() => { if (!cancelled) setFlagsChecked(true); });
    return () => { cancelled = true; };
  }, []);

  const fetchList = useCallback(async (nextOffset = 0, nextApplied = applied) => {
    if (!canView || !enabled) return;
    try {
      setLoading(true);
      setErr(null);
      const resp = await listReviewOutcomes({
        ...nextApplied,
        limit: LIMIT,
        offset: nextOffset,
      });
      setRows(resp.items || []);
      setTotal(resp.total || 0);
      setOffset(resp.offset || nextOffset);
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      if (code === 'FEATURE_DISABLED') {
        setEnabled(false);
      } else {
        setErr(e?.response?.data?.error?.message || 'Failed to load review outcomes');
      }
    } finally {
      setLoading(false);
    }
  }, [applied, canView, enabled]);

  // Day 10 — pull a wider dataset for the analytics strip. Runs when the
  // *applied* filters change (not on pagination). Fails silently.
  const fetchAnalytics = useCallback(async (nextApplied = applied) => {
    if (!canView || !enabled) return;
    try {
      setAnalyticsLoading(true);
      const resp = await listReviewOutcomes({
        ...nextApplied,
        limit: ANALYTICS_CAP,
        offset: 0,
      });
      setAnalyticsItems(resp.items || []);
      setAnalyticsTotal(resp.total || 0);
    } catch {
      setAnalyticsItems([]);
      setAnalyticsTotal(0);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [applied, canView, enabled]);

  useEffect(() => {
    if (flagsChecked && enabled && canView) {
      fetchList(0, applied);
      fetchAnalytics(applied);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsChecked, enabled, canView]);

  const submitFilters = (e) => {
    e?.preventDefault?.();
    const next = {
      q: q.trim(),
      date_from: dateFrom ? new Date(dateFrom + 'T00:00:00').toISOString() : '',
      date_to: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : '',
      chart_type: chartType === 'all' ? '' : chartType,
    };
    setApplied(next);
    fetchList(0, next);
    fetchAnalytics(next);
  };

  const resetFilters = () => {
    setQ(''); setDateFrom(''); setDateTo(''); setChartType('all');
    const empty = { q: '', date_from: '', date_to: '', chart_type: '' };
    setApplied(empty);
    fetchList(0, empty);
    fetchAnalytics(empty);
  };

  const page = Math.floor(offset / LIMIT) + 1;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));
  const hasActiveFilter = useMemo(() => (
    Boolean(applied.q || applied.date_from || applied.date_to || applied.chart_type)
  ), [applied]);

  // ── Guards ──
  if (!canView) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="explorer-no-access">
          You don't have permission to view review outcomes.
        </CardContent></Card>
      </div>
    );
  }
  if (flagsChecked && !enabled) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="explorer-flag-off">
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Review outcomes explorer is not enabled for your organisation yet.</p>
            <p className="text-xs text-muted-foreground">Ask an administrator to enable the "Clinical Chart Review" feature flag.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="review-outcomes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Review outcomes
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Completed clinical chart reviews in your scope. Read-only insight.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { fetchList(offset, applied); fetchAnalytics(applied); }} data-testid="explorer-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
        </Button>
      </div>

      {/* Day 10 — Visual analytics above filters (uses same applied filters) */}
      <ReviewOutcomesAnalytics
        items={analyticsItems}
        loading={analyticsLoading}
        total={analyticsTotal}
        cap={ANALYTICS_CAP}
      />

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitFilters} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Keyword (outcome note)</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. pain, routine, referral" className="pl-7 h-8 text-sm" data-testid="explorer-q" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Reviewed from</label>
              <TrinitiDatePicker value={dateFrom} onChange={(v) => setDateFrom(v)} className="h-8 text-sm" testId="explorer-date-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Reviewed to</label>
              <TrinitiDatePicker value={dateTo} onChange={(v) => setDateTo(v)} className="h-8 text-sm" testId="explorer-date-to" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Chart type</label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="h-8 text-sm" data-testid="explorer-chart-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All chart types</SelectItem>
                  <SelectItem value="bowel_chart">Bowel Chart</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 justify-end">
              <Button type="button" size="sm" variant="outline" onClick={resetFilters} disabled={!hasActiveFilter && !q && !dateFrom && !dateTo && chartType === 'all'} data-testid="explorer-reset">
                Reset
              </Button>
              <Button type="submit" size="sm" data-testid="explorer-apply">
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                Apply
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {loading && rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="explorer-loading">Loading…</div>
          ) : err ? (
            <div className="py-10 text-center text-sm text-red-600" data-testid="explorer-error">{err}</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center space-y-1" data-testid="explorer-empty">
              <p className="text-sm text-muted-foreground">No completed review outcomes found.</p>
              <p className="text-xs text-muted-foreground">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                    <th className="text-left px-3 py-2">Participant</th>
                    <th className="text-left px-3 py-2">Chart</th>
                    <th className="text-left px-3 py-2">Reviewed</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="text-left px-3 py-2 min-w-[240px]">Outcome</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.chart_id} className="border-b hover:bg-muted/30" data-testid={`explorer-row-${r.chart_id}`}>
                      <td className="px-3 py-2 font-medium">{r.participant_name}</td>
                      <td className="px-3 py-2">
                        <div>{r.chart_name}</div>
                        <div className="text-[10px] text-muted-foreground">{r.chart_template_code}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid={`explorer-row-completed-${r.chart_id}`}>{formatDateTime(r.review_completed_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.review_completed_by?.name || '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.review_outcome_note ? (
                          <span title={r.review_outcome_note} data-testid={`explorer-row-note-${r.chart_id}`}>{clamp(r.review_outcome_note)}</span>
                        ) : <span className="italic">(no note)</span>}
                      </td>
                      <td className="px-3 py-2">
                        {r.status === 'archived' ? (
                          <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600">Archived</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-700">Active</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(r.detail_url)} data-testid={`explorer-row-open-${r.chart_id}`}>
                          <ExternalLink className="w-3 h-3 mr-1" />Open chart
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="explorer-pagination">
          <div>Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}</div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={offset === 0 || loading}
              onClick={() => fetchList(Math.max(0, offset - LIMIT), applied)} data-testid="explorer-prev">
              <ChevronLeft className="w-3 h-3 mr-1" />Previous
            </Button>
            <span className="px-2">Page {page} of {pageCount}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={offset + LIMIT >= total || loading}
              onClick={() => fetchList(offset + LIMIT, applied)} data-testid="explorer-next">
              Next<ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
